/**
 * Shared Toast Utility
 * Consistent toast notifications across the child-app.
 *
 * Usage:
 *   import { showToast } from '../utils/toast.js';
 *   showToast('Message', 'success');  // green
 *   showToast('Error', 'error');      // orange/red
 *   showToast('Info', 'info');        // blue
 */

/**
 * Token → asset map for copy that needs to show a real button inline, e.g.
 * "Druk die {record} om te begin opneem". Keeps the strings translator-friendly
 * and shows the actual button rather than an emoji approximation.
 *
 * Single source of truth: the recording coach renders the same tokens.
 */
const ICON_TOKENS = {
  '{record}': '/svg/buttons/RecordButton-2.svg',
  '{stop}': '/svg/buttons/StopButton-2.svg',
  '{star}': '/svg/components/YellowStar.svg',
  '{tick}': '/svg/buttons/Tick.svg',
};

const TOKEN_SPLIT_PATTERN = /(\{record\}|\{stop\}|\{star\}|\{tick\})/g;
/** Non-global twin, so testing for a token cannot advance a shared lastIndex. */
const HAS_TOKEN_PATTERN = /\{record\}|\{stop\}|\{star\}|\{tick\}/;

/**
 * Whether a string contains any icon token.
 * @param {string} text
 * @returns {boolean}
 */
export function hasIconToken(text) {
  return HAS_TOKEN_PATTERN.test(String(text));
}

/**
 * Render copy, swapping icon tokens for real button images.
 *
 * Builds DOM nodes rather than assigning innerHTML, so translated copy can
 * never inject markup.
 *
 * @param {string} text - Copy, optionally containing icon tokens
 * @param {string} iconClass - Class applied to each generated image, so callers
 *   can size the icons to their own typography
 * @returns {DocumentFragment}
 */
export function renderTokenisedMessage(text, iconClass) {
  const fragment = document.createDocumentFragment();

  String(text)
    .split(TOKEN_SPLIT_PATTERN)
    .filter((part) => part !== '')
    .forEach((part) => {
      const src = ICON_TOKENS[part];
      if (src) {
        if (part === '{tick}') {
          // Render tick as a styled text character so color/size apply via CSS
          const span = document.createElement('span');
          span.className = 'kassie-toast__tick';
          span.textContent = '✔';
          span.setAttribute('aria-hidden', 'true');
          fragment.appendChild(span);
        } else {
          const img = document.createElement('img');
          img.src = src;
          img.alt = '';
          img.className = iconClass;
          img.setAttribute('aria-hidden', 'true');
          fragment.appendChild(img);
        }
      } else {
        fragment.appendChild(document.createTextNode(part));
      }
    });

  return fragment;
}

/**
 * Build one line of toast copy.
 *
 * Uses textContent (or DOM nodes for tokenised copy), never innerHTML: the copy
 * comes from translation files and must never be parsed as markup.
 *
 * @param {string} text
 * @returns {HTMLElement}
 */
function buildLine(text) {
  const line = document.createElement('div');
  line.className = 'kassie-toast__line';

  if (hasIconToken(text)) {
    line.appendChild(renderTokenisedMessage(text, 'kassie-toast__token-icon'));
  } else {
    line.textContent = text;
  }

  return line;
}

/**
 * Build a decorative leading icon.
 *
 * Marked aria-hidden with an empty alt: it repeats what the copy already says,
 * so announcing it would just be noise.
 *
 * @param {string} src
 * @returns {HTMLImageElement}
 */
function buildIcon(src) {
  const img = document.createElement('img');
  img.src = src;
  img.alt = '';
  img.width = 28;
  img.height = 28;
  img.className = 'kassie-toast__icon';
  img.setAttribute('aria-hidden', 'true');
  return img;
}

/**
 * Show a toast message.
 * @param {string} message - Text to display
 * @param {'success'|'error'|'info'} [type='info'] - Toast style
 * @param {number} [durationMs=2000] - Auto-dismiss time in ms
 * @param {object} [options] - Optional extras
 * @param {HTMLElement} [options.anchorEl] - If provided, position the toast against
 *   this element instead of the default screen-centered position.
 * @param {'below'|'above'} [options.placement='below'] - Which side of `anchorEl`
 *   to sit on. Ignored without `anchorEl`.
 * @param {boolean} [options.wiggle] - If true, play a brief 300ms wiggle animation
 *   when the toast appears (used to draw attention, e.g. anchored to a button).
 * @param {HTMLElement} [options.parent=document.body] - Where to mount the toast.
 *   Pass a node inside the current view to tie the toast's lifetime to that view,
 *   so navigating away removes it instead of leaving it over the next page.
 *   Avoid passing an element that has a transform/filter (including on hover) —
 *   those create a containing block and would break the fixed positioning.
 * @param {boolean} [options.persist] - Skip the auto-dismiss timer. `durationMs` is
 *   then ignored and the caller owns the toast's lifetime, so it MUST remove it
 *   with dismissToast() — otherwise the toast outlives the view that opened it.
 * @param {boolean} [options.stack] - Render as a top-anchored column that grows
 *   downward, so further lines can be added with appendToToast().
 * @param {string} [options.icon] - URL of a decorative icon to sit before the
 *   copy. Rendered aria-hidden, since it repeats what the copy says.
 * @param {boolean} [options.atTop] - Pin the toast near the top of the viewport
 *   (--toast-top) instead of centring it. Use where a centred toast would cover
 *   the content it is commenting on, e.g. the memory game cards. Ignored when
 *   `anchorEl` or `stack` is used, since those set their own position.
 * @returns {HTMLElement|undefined} The toast element, for appendToToast()/dismissToast().
 */
export function showToast(message, type = 'info', durationMs = 2000, options = {}) {
  // Guard: skip if no DOM available (e.g. during tests without jsdom on this module)
  if (typeof document === 'undefined') return;

  const { anchorEl, wiggle, parent, persist, stack, placement, icon, atTop } = options;

  // Remove any existing toast
  const existing = document.querySelector('.kassie-toast');
  if (existing) existing.remove();

  const toast = document.createElement('div');
  toast.className = `kassie-toast kassie-toast--${type}`;
  toast.setAttribute('role', 'status');
  toast.setAttribute('aria-live', 'polite');

  if (stack) {
    // Stacked toasts hold their copy in line elements so further lines can be
    // appended later. Single-line toasts keep the plain text node they have
    // always used — no reason to change the markup the other callers rely on.
    toast.classList.add('kassie-toast--stack');
    toast.appendChild(buildLine(message));
  } else if (icon) {
    toast.classList.add('kassie-toast--with-icon');
    toast.appendChild(buildIcon(icon));
    toast.appendChild(document.createTextNode(message));
  } else if (hasIconToken(message)) {
    // Copy that shows a button inline, e.g. "Druk die {record} om te begin".
    // Deliberately not the flex --with-icon layout: the icon has to sit inside
    // the sentence, which needs normal inline flow.
    toast.classList.add('kassie-toast--tokenised');
    toast.appendChild(renderTokenisedMessage(message, 'kassie-toast__token-icon'));
  } else {
    toast.textContent = message;
  }

  // Anchored and stacked toasts already own their vertical position, so the
  // top pin would only fight them.
  if (atTop && !anchorEl && !stack) {
    toast.classList.add('kassie-toast--top');
  }

  if (anchorEl && typeof anchorEl.getBoundingClientRect === 'function') {
    const gap = 8; // px gap between the anchor and the toast
    const rect = anchorEl.getBoundingClientRect();
    toast.classList.add('kassie-toast--anchored');
    toast.style.left = `${rect.left + rect.width / 2}px`;

    if (placement === 'above') {
      // Measured from the anchor rather than a fixed offset, because the
      // reader footer is 10vh on phones and 15vh on tablet and desktop.
      toast.classList.add('kassie-toast--anchored-above');
      toast.style.bottom = `${window.innerHeight - rect.top + gap}px`;
    } else {
      toast.style.top = `${rect.bottom + gap}px`;
    }
  }

  (parent || document.body).appendChild(toast);

  // Animate in
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      toast.classList.add('kassie-toast--visible');
      if (wiggle) {
        toast.classList.add('kassie-toast--wiggle');
        setTimeout(() => toast.classList.remove('kassie-toast--wiggle'), 300);
      }
    });
  });

  // Auto-dismiss, unless the caller owns the toast's lifetime
  if (!persist) {
    setTimeout(() => {
      toast.classList.remove('kassie-toast--visible');
      setTimeout(() => toast.remove(), 250);
    }, durationMs);
  }

  return toast;
}

/**
 * Add a line of copy, and optionally a button, to a toast already on screen.
 *
 * Used to grow a stacked toast through the steps of a sequence rather than
 * replacing it — showToast() removes any existing toast, so calling it again
 * would restart the sequence instead of adding to it.
 *
 * @param {HTMLElement} toastEl - Element returned by showToast()
 * @param {object} content
 * @param {string} [content.text] - Line of copy to append
 * @param {string} [content.buttonLabel] - If given, append a button with this label
 * @param {Function} [content.onButtonClick] - Click handler for that button
 * @returns {void}
 */
export function appendToToast(toastEl, { text, buttonLabel, onButtonClick } = {}) {
  // A toast that has already been dismissed (or was never created, e.g. no DOM)
  // is not an error here — the sequence should degrade quietly.
  if (!toastEl || !toastEl.isConnected) return;

  if (text) {
    toastEl.appendChild(buildLine(text));
  }

  if (buttonLabel) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'kassie-toast__btn';
    btn.textContent = buttonLabel;
    if (typeof onButtonClick === 'function') {
      btn.addEventListener('click', onButtonClick);
    }
    toastEl.appendChild(btn);
  }
}

/**
 * Remove a toast immediately.
 *
 * Required for any toast created with `persist: true`, which has no timer of
 * its own. Also used to clear a toast that would otherwise outlive the view
 * that opened it.
 *
 * Safe to call with null, or on a toast that is already gone.
 *
 * @param {HTMLElement} [toastEl] - Element returned by showToast()
 * @returns {void}
 */
export function dismissToast(toastEl) {
  if (!toastEl) return;
  toastEl.remove();
}

